// IconTextGridSection.js — Grilla de iconos con texto
import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { BACKEND_BASE } from '../../../services/api';
import { buildBg } from '../bgHelpers';
import { subrayadoCss } from '../tituloHelpers';

const Wrapper = styled.section`
  width: 100%;
  padding: ${p => p.$padding || '0 0 80px'};
  ${p => p.$marginTop ? `margin-top: ${p.$marginTop};` : ''}
  ${p => p.$marginBottom ? `margin-bottom: ${p.$marginBottom};` : ''}
  background: ${p => p.$bg || 'transparent'};
  ${p => p.$radius ? `border-radius: ${p.$radius};` : ''}
`;

const BlockTitle = styled.h2`
  text-align: ${p => p.$align || 'center'};
  color: ${p => p.$color || '#0f355b'};
  font-size: ${p => p.$size || '38px'};
  font-weight: ${p => p.$weight || '700'};
  line-height: ${p => p.$lineHeight || '1.2'};
  font-family: ${p => p.$font || "'Myriad Pro', sans-serif"};
  /* Dentro de la caja: ocupa todo el ancho de la grilla. Fuera: centrado al ancho máximo. */
  ${p => p.$inGrid
    ? 'grid-column: 1 / -1; max-width: none; margin: 0;'
    : `max-width: ${p.$maxWidth || '1200px'}; margin: 0 auto;`}
  padding: ${p => p.$padding || (p.$inGrid ? '0 0 24px' : '0 30px 30px')};
  ${p => p.$subrayado || ''}
  @media (max-width: 991px) { font-size: ${p => parseInt(p.$size || 38) * 0.75}px; }
`;

/* Botón opcional al pie de una columna (el «Ingresar» del bloque del curso).
   Solo aparece si ese ítem trae `texto_boton`; el resto de columnas y de
   proyectos se pintan igual que siempre. */
const ItemBtn = styled.button`
  margin-top: ${p => p.$margen || '18px'};
  background: ${p => p.$bg || 'transparent'};
  color: ${p => p.$color || '#f27903'};
  border: ${p => p.$borde || '1px solid #f27903'};
  border-radius: ${p => p.$radio || '999px'};
  padding: ${p => p.$padding || '10px 30px'};
  font-family: inherit;
  font-size: ${p => p.$size || '17px'};
  font-weight: ${p => p.$peso || '400'};
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.85; }
`;

const Container = styled.div`
  width: ${p => p.$width || '100%'};
  max-width: ${p => p.$maxWidth || '1200px'};
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(${p => p.$cols || 3}, 1fr);
  gap: ${p => p.$gap || '0'};
  box-sizing: border-box;
  /* Fondo ajustado al ancho del bloque: la caja (color/imagen + radio + borde +
     padding) se aplica aquí, respetando width/ancho máximo. */
  ${p => p.$boxBg ? `background: ${p.$boxBg};` : ''}
  ${p => p.$boxRadius ? `border-radius: ${p.$boxRadius}; overflow: hidden;` : ''}
  ${p => p.$boxBorder ? `border: ${p.$boxBorder};` : ''}
  ${p => p.$boxPad ? `padding: ${p.$boxPad};` : ''}
  @media (max-width: 991px) {
    padding: ${p => p.$boxPad || '0 30px'};
    grid-template-columns: 1fr;
  }
`;

const Item = styled.div`
  display: flex;
  flex-direction: ${p => p.$layout === 'icono-arriba'
    ? 'column'
    : (p.$layout === 'icono-derecha' ? 'row-reverse' : 'row')};
  align-items: ${p => p.$layout === 'icono-arriba' ? 'center' : 'center'};
  justify-content: ${p => p.$vAlign || 'center'};
  text-align: ${p => p.$layout === 'icono-arriba' ? 'center' : 'left'};
  gap: ${p => p.$gapItem || '12px'};
  font-family: ${p => p.$font || "'Myriad Pro', sans-serif"};
  color: ${p => p.$color || '#0f355b'};
  font-size: ${p => p.$fontSize || '17px'};
  font-weight: ${p => p.$weight || 'inherit'};
  text-transform: ${p => p.$transform || 'none'};
  background: ${p => p.$bg || 'transparent'};
  ${p => p.$border
    ? `border: ${p.$border};`
    : (p.$sinBordeInferior ? '' : `border-bottom: 1px solid ${p.$borderColor || '#14b6cb'};`)}
  border-radius: ${p => p.$radius || '0'};
  padding: ${p => p.$padding || '20px 0'};
  margin: ${p => p.$margin || '0'};
  box-sizing: border-box;
  &:nth-last-child(-n+${p => p.$cols || 3}) {
    ${p => !p.$border && !p.$sinBordeInferior && 'border-bottom: none;'}
  }
  /* Separador vertical entre columnas (estilo «icono | icono | icono»): se pinta
     a la derecha de todos menos del último de cada fila. */
  ${p => p.$sep && !p.$border && `
    &:not(:nth-child(${p.$cols}n)):not(:last-child) {
      border-right: ${p.$sepGrosor || '2px'} solid ${p.$sep};
    }
  `}
  @media (max-width: 991px) {
    justify-content: center;
    text-align: center;
    ${p => p.$sep && 'border-right: none;'}
  }
`;

const Icon = styled.img`
  /* Modo "alto": todos los íconos quedan a la misma altura aunque el ancho de
     cada archivo sea distinto (el ancho se ajusta solo). Modo "ancho": al revés. */
  ${p => p.$modo === 'alto'
    ? `height: ${p.$size || '58px'}; width: auto; max-width: 100%;`
    : `width: ${p.$size || '58px'}; min-width: ${p.$size || '58px'}; height: auto;`}
  object-fit: contain;
  ${p => p.$margin ? `margin: ${p.$margin};` : ''}
`;

const EmojiIcon = styled.span`
  font-size: ${p => p.$size ? `calc(${p.$size} * 0.55)` : '2rem'};
  min-width: ${p => p.$size || '58px'};
  text-align: center;
`;

const Text = styled.div`
  ${p => p.$layout === 'icono-arriba' ? 'width: 100%;' : 'flex: 1;'}
  line-height: ${p => p.$lineHeight || '1.4'};
`;

const Secondary = styled.div`
  margin-top: ${p => p.$mt || '6px'};
  color: ${p => p.$color || '#64748b'};
  font-size: ${p => p.$size || '14px'};
  line-height: ${p => p.$lineHeight || '1.4'};
  font-weight: ${p => p.$weight || '400'};
  text-transform: ${p => p.$transform || 'none'};
  ${p => p.$shadow ? `text-shadow: ${p.$shadow};` : ''}
`;

const IconTextGridSection = ({ config, proyecto, tl }) => {
  const navigate = useNavigate();
  // Las direcciones del propio sitio navegan sin recargar; las externas abren
  // en otra pestaña.
  const irA = (url) => {
    if (!url) return;
    if (/^https?:\/\//i.test(url)) window.open(url, '_blank', 'noopener');
    else navigate(url);
  };
  const items = config.items || [];
  if (items.length === 0) return null;
  const cols = config.columnas || 3;
  const primary = config.color_primario || proyecto?.color_primario || '#0f355b';
  const secondary = config.color_secundario || proyecto?.color_secundario || '#14b6cb';
  const layout = config.layout_item || 'icono-izquierda';
  const iconSize = config.tamano_icono || '58px';
  // 'ancho' (por defecto, comportamiento de siempre) | 'alto' (misma altura para
  // todos los íconos aunque los archivos tengan anchos distintos)
  const iconModo = config.tamano_icono_modo === 'alto' ? 'alto' : 'ancho';
  // Por defecto cada item lleva una línea inferior; se puede quitar para usar
  // separadores verticales entre columnas.
  const sinBordeInferior = config.borde_items === 'ninguno';
  const mostrarTitulo = config.mostrar_titulo !== false && !!config.titulo;
  // Fondo ajustado al ancho del bloque (igual que en el bloque de texto)
  const fondoAjustado = config.fondo_ajustado === true;
  const bgValue = buildBg(config, '#fff');
  // El título puede ir dentro del contenedor (con o sin fondo ajustado) o fuera/encima.
  const tituloDentro = config.titulo_dentro_caja === true;

  const renderTitulo = (inGrid) => (
    <BlockTitle
      $inGrid={inGrid}
      $maxWidth={config.ancho_maximo}
      $align={config.alineacion_titulo || 'center'}
      $color={config.color_titulo || primary}
      $size={config.tamano_titulo || '38px'}
      $weight={config.peso_titulo || '700'}
      $lineHeight={config.interlineado_titulo || '1.2'}
      $font={config.fuente_titulo}
      $padding={config.padding_titulo}
      $subrayado={subrayadoCss(config)}
      dangerouslySetInnerHTML={{ __html: tl(config.titulo) }}
    />
  );

  return (
    <Wrapper
      $bg={fondoAjustado ? 'transparent' : bgValue}
      $padding={config.padding}
      $marginTop={config.margen_superior}
      $marginBottom={config.margen_inferior}
      $radius={fondoAjustado ? undefined : config.radio_fondo}
    >
      {mostrarTitulo && !tituloDentro && renderTitulo(false)}
      <Container
        $width={config.ancho}
        $maxWidth={config.ancho_maximo}
        $cols={cols}
        $gap={config.gap}
        $boxBg={fondoAjustado ? bgValue : undefined}
        $boxRadius={fondoAjustado ? config.radio_fondo : undefined}
        $boxBorder={fondoAjustado ? config.borde_fondo : undefined}
        $boxPad={fondoAjustado ? (config.padding_caja || '30px') : undefined}
      >
        {mostrarTitulo && tituloDentro && renderTitulo(true)}
        {items.map((item, i) => {
          const esImagen = item.icono && (item.icono.startsWith('http') || item.icono.startsWith('uploads/'));
          return (
            <Item
              key={i}
              $layout={layout}
              $gapItem={config.gap_item}
              $font={config.fuente_texto}
              $color={item.color_texto || primary}
              $borderColor={secondary}
              $cols={cols}
              $bg={item.color_fondo}
              $border={item.borde}
              $radius={item.radio}
              $padding={item.padding}
              $margin={item.margen}
              $fontSize={item.tamano_texto || config.tamano_texto}
              $weight={config.peso_texto}
              $transform={config.transform_texto}
              $vAlign={config.alineacion_vertical_items}
              $sinBordeInferior={sinBordeInferior}
              $sep={config.separador_columnas}
              $sepGrosor={config.separador_grosor}
            >
              {item.icono ? (
                esImagen ? (
                  <Icon
                    src={item.icono.startsWith('http') ? item.icono : `${BACKEND_BASE}/${item.icono}`}
                    alt={item.alt || ''}
                    $size={iconSize}
                    $modo={iconModo}
                    $margin={config.margen_icono}
                  />
                ) : (
                  <EmojiIcon $size={iconSize}>{item.icono}</EmojiIcon>
                )
              ) : null}
              <Text $layout={layout} $lineHeight={config.interlineado_texto}>
                <div dangerouslySetInnerHTML={{ __html: tl(item.texto) }} />
                {item.texto_secundario && (
                  <Secondary
                    $color={config.color_texto_secundario || '#64748b'}
                    $size={config.tamano_texto_secundario || '14px'}
                    $lineHeight={config.interlineado_secundario}
                    $weight={config.peso_texto_secundario}
                    $transform={config.transform_texto_secundario}
                    $shadow={config.sombra_texto_secundario}
                    $mt={config.margen_texto_secundario}
                    dangerouslySetInnerHTML={{ __html: tl(item.texto_secundario) }}
                  />
                )}
              </Text>
              {/* Sin destino no se pinta: así el botón no aparece muerto para
                  quien no encaja en ninguna variante (p. ej. una visita sin perfil). */}
              {item.texto_boton && item.url_boton && (
                <ItemBtn
                  type="button"
                  onClick={() => irA(item.url_boton)}
                  $margen={config.margen_boton_item}
                  $bg={config.color_boton_item}
                  $color={config.color_texto_boton_item}
                  $borde={config.borde_boton_item}
                  $radio={config.radio_boton_item}
                  $padding={config.padding_boton_item}
                  $size={config.tamano_boton_item}
                  $peso={config.peso_boton_item}
                >
                  {tl(item.texto_boton)}
                </ItemBtn>
              )}
            </Item>
          );
        })}
      </Container>
    </Wrapper>
  );
};

export default IconTextGridSection;
